import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { COMPANY_INFO, SERVICES_DATA } from '../data/companyData';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const location = useLocation();
  const isContactPage = location.pathname === '/kontakt';

  return (
    <footer className="bg-neutral-950 text-neutral-300 border-t border-neutral-800">
      {/* Top Pre-Footer Callout */}
      {!isContactPage && (
        <div className="border-b border-neutral-800/80 bg-neutral-900/60">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-12">
            <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
              <div>
                <span className="inline-block text-xs font-semibold text-[#DFBF77] uppercase tracking-wider">
                  Professionel Klimateknik
                </span>
                <h2 className="mt-2 pt-0.5 text-2xl sm:text-3xl font-bold tracking-tight text-white">
                  Klar til at optimere dit køle- og klimaanlæg?
                </h2>
                <p className="mt-2 text-sm text-neutral-400 max-w-2xl">
                  Få uforpligtende rådgivning om driftssikkerhed, energieffektivitet og langsigtede energibesparelser.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-[#C5A059] hover:bg-[#DFBF77] text-neutral-950 font-bold text-sm transition-all shadow-md"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>

                <a
                  href={COMPANY_INFO.phoneHref}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-white font-semibold text-sm transition-all border border-neutral-700 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone text-xs text-[#DFBF77]" aria-hidden="true" />
                  <span className="whitespace-nowrap">{COMPANY_INFO.phoneFormatted}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Footer Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand & Info Column */}
          <div className="lg:col-span-2 space-y-4">
            <Logo variant="light" />
            <p className="text-sm text-neutral-400 leading-relaxed max-w-sm pt-2">
              Morine Køl & Klimateknik rådgiver, installerer og servicerer energieffektive køle- og klimaanlæg til erhverv, butikker og industri med fokus på driftssikkerhed og grøn omstilling.
            </p>

            <div className="pt-3 space-y-2.5">
              {/* CVR */}
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-neutral-900/80 border border-neutral-800 text-xs text-neutral-300">
                <i className="fa-solid fa-id-card text-[#DFBF77]" aria-hidden="true" />
                <span>CVR-nr: <strong className="text-white font-semibold">{COMPANY_INFO.cvr}</strong></span>
              </div>

              {/* Opening Hours & 24/7 Badge */}
              <div className="p-3.5 rounded-xl bg-neutral-900/70 border border-neutral-800/90 text-xs space-y-2">
                <div className="flex items-start gap-2.5">
                  <div className="w-6 h-6 rounded-md bg-[#8D6D2F]/20 border border-[#DFBF77]/30 flex items-center justify-center shrink-0 mt-0.5">
                    <i className="fa-solid fa-clock text-[#DFBF77] text-xs" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="font-semibold text-neutral-200">Åbningstider</div>
                    <div className="text-neutral-400">Mandag – Fredag: <span className="text-neutral-200 font-medium">07.00 – 16.00</span></div>
                  </div>
                </div>

                <div className="pt-2 border-t border-neutral-800/80 flex items-center gap-2 text-[11px] text-emerald-400">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                  <span className="font-medium text-neutral-300">Døgnvagt <span className="text-neutral-400">(ved akutte serviceaftaler)</span></span>
                </div>
              </div>
            </div>
          </div>

          {/* Services Links */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#DFBF77] mb-4">
              Ydelser
            </h3>
            <ul className="space-y-2.5 text-sm">
              {SERVICES_DATA.slice(0, 4).map((s) => (
                <li key={s.slug}>
                  <Link to={`/ydelser/${s.slug}`} className="text-neutral-400 hover:text-white transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-[#DFBF77] mb-4">
              Specialer & Kontakt
            </h3>
            <ul className="space-y-2.5 text-sm mb-6">
              {SERVICES_DATA.slice(4).map((s) => (
                <li key={s.slug}>
                  <Link to={`/ydelser/${s.slug}`} className="text-neutral-400 hover:text-white transition-colors">
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>

            <div className="space-y-2 text-xs">
              <a
                href={COMPANY_INFO.phoneHref}
                className="flex items-center gap-2 text-neutral-300 hover:text-[#DFBF77] transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-phone text-[#C5A059]" aria-hidden="true" />
                <span className="whitespace-nowrap font-medium">{COMPANY_INFO.phoneFormatted}</span>
              </a>
              <a
                href={COMPANY_INFO.emailHref}
                className="flex items-center gap-2 text-neutral-300 hover:text-[#DFBF77] transition-colors whitespace-nowrap"
              >
                <i className="fa-solid fa-envelope text-[#C5A059]" aria-hidden="true" />
                <span className="whitespace-nowrap">{COMPANY_INFO.email}</span>
              </a>
              <div className="flex items-start gap-2 text-neutral-400">
                <i className="fa-solid fa-location-dot text-[#C5A059] mt-0.5" aria-hidden="true" />
                <span>{COMPANY_INFO.address.full}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Copyright */}
        <div className="mt-12 pt-8 border-t border-neutral-900 flex flex-col lg:flex-row items-center justify-between gap-3 sm:gap-4 text-center lg:text-left text-xs text-neutral-400">
          <p>© {currentYear} {COMPANY_INFO.name}. Alle rettigheder forbeholdes.</p>
          <p className="flex flex-wrap items-center justify-center lg:justify-end gap-2">
            <span>Professionelt køle- og klimateknisk anlæg</span>
            <span>•</span>
            <span className="text-[#DFBF77]">Hillerød, Nordsjælland</span>
          </p>
        </div>
      </div>
    </footer>
  );
}
